'use client';

import { FC, useState } from 'react';

import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { DisplayOrder } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { FlyoutContent } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutContent';
import { FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutHeader';
import { useTranslations } from '@ravago/shared/translations/client';

import { OrderLineFlyout } from '../order-line-flyout/order-line-flyout';

import { DesktopShipmentTable, ShipmentTableProps } from './desktop-shipment-table';
import { MobileShipmentTable } from './mobile-shipment-table';

export const ShipmentTable: FC<ShipmentTableProps> = ({
   shipments,
   locale,
   isConsumer,
   acceptWebp,
   openedFrom,
   isUSBusinessContext,
   showInvoiceDocuments,
   requestCoa,
   requestDeliveryNote,
   orderBaseUrls,
   requestBol,
   downloadDocument
}) => {
   const translate = useTranslations();
   const [flyoutOpen, setFlyoutOpen] = useState(false);
   const [orderNumber, setOrderNumber] = useState<string | null>(null);
   const [contentfulProduct, setContentfulProduct] = useState<NormalizedProduct | undefined>(undefined);

   const onOrderLineButtonClick = async (orderLine: DisplayOrder) => {
      if (isConsumer) {
         setOrderNumber(orderLine.orderNumber);
         setContentfulProduct(orderLine.contentfulProduct);
         setFlyoutOpen(true);
      }
   };

   return (
      <>
         <Box margin={{ top: 'md' }} direction="column" gap="sm" width="100%">
            {shipments.map((shipment) => (
               <BreakpointSwitch key={shipment.shipmentId} includeViews={['desktop']}>
                  <DesktopShipmentTable
                     shipment={shipment}
                     locale={locale}
                     acceptWebp={acceptWebp}
                     orderBaseUrls={orderBaseUrls}
                     onOrderLineButtonClick={onOrderLineButtonClick}
                     openedFrom={openedFrom}
                  />
               </BreakpointSwitch>
            ))}
            {shipments.map((shipment) => (
               <BreakpointSwitch key={shipment.shipmentId} includeViews={['phone', 'tab-port', 'tab-land']}>
                  <MobileShipmentTable
                     shipment={shipment}
                     locale={locale}
                     acceptWebp={acceptWebp}
                     orderBaseUrls={orderBaseUrls}
                     openedFrom={openedFrom}
                     onOrderLineButtonClick={onOrderLineButtonClick}
                  />
               </BreakpointSwitch>
            ))}
         </Box>
         {orderNumber && (
            <Flyout open={flyoutOpen} closeOnOutsideClick onFlyoutClose={() => setFlyoutOpen(false)}>
               <FlyoutHeader>
                  {translate('page-consumer.order-line-flyout.order')} {orderNumber}
               </FlyoutHeader>
               <FlyoutContent>
                  <OrderLineFlyout
                     locale={locale}
                     isUSBusinessContext={isUSBusinessContext}
                     fullOrderNumber={orderNumber}
                     contentfulProduct={contentfulProduct}
                     openedFrom={openedFrom}
                     orderBaseUrls={orderBaseUrls}
                     showInvoiceDocuments={showInvoiceDocuments}
                     requestCoa={requestCoa}
                     requestBol={requestBol}
                     requestDeliveryNote={requestDeliveryNote}
                     downloadDocument={downloadDocument}
                  />
               </FlyoutContent>
            </Flyout>
         )}
      </>
   );
};
